import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import { useAssetConfig } from "@/hooks/useAssetConfig";
import api from "@/lib/api";
import {
  HardHat, MapPin, Fuel, Send, QrCode, Loader2, X, Plus,
  Activity, CheckCircle, Clock, Droplet, Truck, Settings,
  Sparkles, Bird, Wrench, ArrowLeft, LogOut, Hand, PlusCircle,
  Zap, ChevronRight, Settings2, Trash2, Navigation, Shield,
  Route, Key, Save, AlertTriangle, ArrowRightLeft, BookOpen
} from "lucide-react";
import TutorialLedger, { TutorialToggle } from "@/components/shared/TutorialLedger";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher";

export default function MachinistDashboard() {
  const { session, setSession, hubData, refreshData, logout } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const ac = useAssetConfig(session);
  const nav = useNavigate();
  const { sites, machines, drivers, requests } = hubData;
  const myName = session?.userName || "Machinist";

  const [activeSiteId, setActiveSiteId] = useState(null);
  const [activeMachine, setActiveMachine] = useState(null);
  const [fuelLevel, setFuelLevel] = useState(50);
  const [needsAdBlue, setNeedsAdBlue] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [toast, setToast] = useState(null);
  const [statusMachine, setStatusMachine] = useState(null);
  const [statusType, setStatusType] = useState("NORMAL");
  const [statusNotes, setStatusNotes] = useState("");
  const [showExternal, setShowExternal] = useState(false);
  const [extType, setExtType] = useState("CRANE");
  const [extId, setExtId] = useState("");

  // NEW: Request type state
  const [requestType, setRequestType] = useState(() => session?.asset_config?.request_type_primary || "FUEL");
  const [targetSiteId, setTargetSiteId] = useState("");
  const [requestNotes, setRequestNotes] = useState("");
  const [selectedAction, setSelectedAction] = useState("");

  // NEW: Site editing state
  const [editingSite, setEditingSite] = useState(false);
  const [siteAccessCode, setSiteAccessCode] = useState("");
  const [siteAccessRoute, setSiteAccessRoute] = useState("");
  const [sitePhase, setSitePhase] = useState("");
  const [savingSite, setSavingSite] = useState(false);

  // NEW: GPS state
  const [gpsCoords, setGpsCoords] = useState({ lat: 0, lon: 0 });
  const [gpsLoading, setGpsLoading] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };
  const activeSite = sites.find(s => s.id === activeSiteId);
  const siteMachines = useMemo(() => machines.filter(m => m.site_id === activeSiteId), [machines, activeSiteId]);
  const otherSites = useMemo(() => sites.filter(s => s.id !== activeSiteId), [sites, activeSiteId]);

  const myRecentRequests = useMemo(() =>
    requests.filter(r => r.requester_type === "MACHINIST" || r.requester_type === "machinist")
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  , [requests]);

  // Auto-detect GPS on mount
  const detectGPS = useCallback(() => {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setGpsLoading(false);
      },
      () => setGpsLoading(false),
      { enableHighAccuracy: true, timeout: 5000 }
    );
  }, []);

  useEffect(() => { detectGPS(); }, [detectGPS]);

  // Load site editing fields
  useEffect(() => {
    if (activeSite) {
      setSiteAccessCode(activeSite.lock_codes || "");
      setSiteAccessRoute(activeSite.accessibility_route || "");
      setSitePhase(activeSite.current_stage || "");
    }
  }, [activeSite]);

  const handleSaveSiteInfo = async () => {
    if (!activeSite) return;
    setSavingSite(true);
    try {
      await api.updateSite(activeSite.id, {
        lock_codes: siteAccessCode,
        accessibility_route: siteAccessRoute,
        current_stage: sitePhase,
      });
      await api.createEvent({
        hub_id: session.hubId, event_type: "SITE_UPDATE",
        message: `${myName} updated site info for ${activeSite.name}${sitePhase ? ` (Phase: ${sitePhase})` : ""}`,
        site_id: activeSite.id, site_name: activeSite.name,
      });
      showToast("Site info saved!");
      setEditingSite(false);
      refreshData();
    } catch (e) { console.error(e); }
    setSavingSite(false);
  };

  const handleQRScan = () => {
    if (siteMachines.length === 0) return;
    const target = siteMachines[Math.floor(Math.random() * siteMachines.length)];
    openRequestModal(target, "FUEL");
  };

  const openRequestModal = (machine, type = "FUEL") => {
    setActiveMachine(machine);
    setFuelLevel(machine.fuel_level_percent || 50);
    setNeedsAdBlue(machine.needs_adblue || false);
    setRequestType(type);
    setTargetSiteId("");
    setRequestNotes("");
    setIsRequesting(true);
    detectGPS();
  };

  const handleSubmitRequest = async () => {
    if (!activeMachine || !activeSite) return;
    // Build products array dynamically from asset config
    let products = [];
    if (requestType === ac.requestType) {
      if (ac.isTaskBased) {
        products = selectedAction ? [selectedAction] : [ac.itemA];
      } else {
        products = [ac.itemA];
        if (needsAdBlue && ac.itemBEnabled) products.push(ac.itemB);
        if (ac.itemCEnabled) products.push(ac.itemC);
      }
    }
    const payload = {
      hub_id: session.hubId,
      machine_id: activeMachine.id,
      machine_number: activeMachine.machine_number,
      site_id: activeSite.id,
      site_name: activeSite.name,
      request_type: requestType,
      urgency: requestType === ac.requestType && !ac.isTaskBased && fuelLevel < 20 ? "URGENT" : requestType === "REPARATION" ? "URGENT" : "NONE",
      products,
      notes: requestNotes || `${requestType === ac.requestType ? ac.requestLabel : requestType} requested by ${myName}${selectedAction ? ` — ${selectedAction}` : ""}`,
      requester_type: "MACHINIST",
      current_fuel_level: requestType === ac.requestType && !ac.isTaskBased ? fuelLevel : 0,
      gps_lat: gpsCoords.lat,
      gps_lon: gpsCoords.lon,
      target_site_id: requestType === "TRANSPORT" ? targetSiteId : "",
      target_site_name: requestType === "TRANSPORT" ? (sites.find(s => s.id === targetSiteId)?.name || "") : "",
    };
    await api.createRequest(payload);
    showToast(`${requestType === ac.requestType ? ac.requestLabel : requestType} sent!`);
    setActiveMachine(null);
    setIsRequesting(false);
    setSelectedAction("");
    refreshData();
  };

  const handleStatusSubmit = async () => {
    if (!statusMachine) return;
    await api.updateMachine(statusMachine.id, {
      status_type: statusType, status_notes: statusNotes
    });
    await api.createEvent({
      hub_id: session.hubId, event_type: "MACHINE_STATUS_CHANGE",
      message: `${statusMachine.machine_number} status: ${statusType} - ${statusNotes}`,
      machine_id: statusMachine.id, machine_number: statusMachine.machine_number
    });
    showToast("Status updated!");
    setStatusMachine(null);
    setStatusNotes("");
    refreshData();
  };

  const handleRegisterExternal = async () => {
    if (!activeSiteId || !extId) return;
    await api.createMachine({
      hub_id: session.hubId, machine_number: `EXT-${extId.toUpperCase()}`,
      name: `${extType} ${extId}`, site_id: activeSiteId,
      is_external: true, external_type: extType, is_rental: true,
      fuel_level_percent: 50, needs_adblue: false
    });
    showToast("Machine registered!");
    setExtId(""); setShowExternal(false);
    refreshData();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "DEFECT": return "bg-red-600 text-white";
      case "TRANSPORT": return "bg-blue-600 text-white";
      case "UPKEEP": return "bg-purple-600 text-white";
      default: return "bg-lime-50 text-lime-600";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "DEFECT": return <Wrench className="w-3 h-3" />;
      case "TRANSPORT": return <Truck className="w-3 h-3" />;
      case "UPKEEP": return <Settings className="w-3 h-3" />;
      default: return <CheckCircle className="w-3 h-3" />;
    }
  };

  const getReqTypeIcon = (type) => {
    switch (type) { case "REPARATION": return <Wrench className="w-3 h-3" />; case "MAINTENANCE": return <Settings className="w-3 h-3" />; case "TRANSPORT": return <ArrowRightLeft className="w-3 h-3" />; default: return <Fuel className="w-3 h-3" />; }
  };

  const getReqColor = (type) => {
    switch (type) { case "REPARATION": return "text-red-400 bg-red-500/10"; case "MAINTENANCE": return "text-purple-400 bg-purple-500/10"; case "TRANSPORT": return "text-blue-400 bg-blue-500/10"; default: return "text-lime-600 bg-lime-50"; }
  };

  // Site selection screen
  if (!activeSiteId) {
    return (
      <div className="min-h-[100dvh] bg-white flex flex-col items-center justify-center p-6" data-testid="site-selection">
        <div className="max-w-md w-full space-y-8 animate-fadeInUp">
          <div className="text-center">
            <div className="w-20 h-20 bg-lime-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
              <MapPin className="w-10 h-10 text-slate-950" />
            </div>
            <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900">{t("select_site")}</h2>
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-2">{myName} / {session?.hubName}</p>
            {gpsCoords.lat !== 0 && (
              <p className="text-[10px] text-lime-500 font-mono mt-2 flex items-center justify-center gap-1">
                <Navigation className="w-3 h-3" /> GPS: {gpsCoords.lat.toFixed(4)}, {gpsCoords.lon.toFixed(4)}
              </p>
            )}
          </div>
          <div className="grid gap-3">
            {sites.length === 0 ? (
              <div className="p-10 border border-dashed border-slate-200 rounded-xl text-center">
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">{t("no_data")}</p>
              </div>
            ) : sites.map((site, i) => (
              <button key={site.id} data-testid={`select-site-${site.id}`}
                onClick={() => setActiveSiteId(site.id)}
                className={`group w-full p-5 bg-slate-50 border border-slate-200 rounded-xl hover:border-lime-400 transition-all text-left flex items-center justify-between active:scale-[0.98] animate-fadeInUp stagger-${i + 1}`}
                style={{ opacity: 0 }}>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight group-hover:text-lime-600 transition-colors">{site.name}</h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{site.city} / {site.code}</p>
                  {site.gps_lat > 0 && (
                    <p className="text-[9px] text-slate-400 font-mono mt-1 flex items-center gap-1">
                      <Navigation className="w-2.5 h-2.5" /> {site.gps_lat.toFixed(3)}, {site.gps_lon.toFixed(3)}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-lime-500 group-hover:translate-x-1 transition-all" />
              </button>
            ))}
          </div>
          <button onClick={() => { logout(); nav("/"); }}
            className="w-full py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-slate-900 transition-all flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {t("back")}
          </button>
          <button onClick={() => { setSession(null); nav("/portal"); }} data-testid="back-to-lobby-btn"
            className="w-full py-3 bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-slate-900 transition-all flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Lobby
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto min-h-[100dvh] bg-white pb-32 relative" data-testid="machinist-dashboard">
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 left-4 right-4 z-[1000] bg-lime-500 text-slate-950 px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 font-bold text-sm uppercase tracking-wider animate-slideInDown max-w-lg mx-auto">
          <Sparkles className="w-4 h-4" /> {toast}
        </div>
      )}

      <TutorialLedger role="machinist" isOpen={showTutorial} onClose={() => setShowTutorial(false)} />

      {/* REQUEST MODAL (all types) */}
      {activeMachine && isRequesting && (
        <div className="fixed inset-0 z-[400] bg-white/95 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-slate-50 rounded-xl w-full max-w-sm border border-slate-200 animate-scaleIn max-h-[90vh] overflow-y-auto">
            <div className="bg-white p-5 flex justify-between items-center border-b border-slate-200 sticky top-0 z-10">
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">New Request</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{activeMachine.machine_number} / {activeMachine.name}</p>
              </div>
              <button onClick={() => setIsRequesting(false)} className="p-2 bg-slate-100 rounded-xl text-slate-400" data-testid="close-request-modal"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              {/* Request Type Selector */}
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Request Type</label>
                <div className="grid grid-cols-2 gap-2" data-testid="request-type-selector">
                  {[
                    { id: ac.requestType, label: ac.requestLabel.replace(" Request", ""), icon: <Fuel className="w-4 h-4" />, c: "lime" },
                    { id: "REPARATION", label: "Repair", icon: <Wrench className="w-4 h-4" />, c: "red" },
                    { id: "MAINTENANCE", label: "Maintenance", icon: <Settings className="w-4 h-4" />, c: "purple" },
                    { id: "TRANSPORT", label: "Transport", icon: <ArrowRightLeft className="w-4 h-4" />, c: "blue" },
                  ].map(opt => (
                    <button key={opt.id} data-testid={`req-type-${opt.id}`}
                      onClick={() => setRequestType(opt.id)}
                      className={`p-3 rounded-xl text-xs font-bold uppercase border flex items-center gap-2 transition-all ${
                        requestType === opt.id
                          ? `border-${opt.c}-500 bg-${opt.c}-500/10 text-${opt.c}-400`
                          : "border-slate-200 bg-white text-slate-400"
                      }`}>
                      {opt.icon} {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Supply-specific fields — dynamic based on hub type */}
              {requestType === ac.requestType && (
                <>
                  {ac.isTaskBased ? (
                    /* Action Hub: show action selector */
                    <div>
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Select Action</label>
                      <div className="space-y-2" data-testid="action-selector">
                        {(ac.actions || []).map((action, i) => (
                          <button key={i} data-testid={`action-${i}`}
                            onClick={() => setSelectedAction(action)}
                            className={`w-full p-3 rounded-xl text-xs font-bold uppercase border text-left flex items-center gap-2 transition-all ${
                              selectedAction === action ? "border-lime-500 bg-lime-500/10 text-lime-600" : "border-slate-200 bg-white text-slate-500"
                            }`}>
                            <CheckCircle className={`w-4 h-4 ${selectedAction === action ? "text-lime-500" : "text-slate-300"}`} />
                            {action}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* Quantity-based hub: show level slider + secondary item toggle */
                    <>
                      <div>
                        <div className="flex justify-between items-end mb-2">
                          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{ac.levelLabel}</span>
                          <span className="text-3xl font-black text-slate-900 font-mono">{fuelLevel}%</span>
                        </div>
                        <input data-testid="fuel-slider" type="range" min="0" max="100" step="5" value={fuelLevel}
                          onChange={e => setFuelLevel(parseInt(e.target.value))}
                          className="w-full h-3 bg-slate-100 rounded-full appearance-none cursor-pointer accent-lime-500" />
                        {fuelLevel < 20 && <p className="text-[10px] text-red-400 font-bold uppercase tracking-wider mt-2 animate-pulse">LOW - URGENT</p>}
                      </div>
                      {ac.itemBEnabled && (
                        <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${needsAdBlue ? "bg-sky-500/20 text-sky-400" : "bg-slate-100 text-slate-400"}`}>
                              <Droplet className="w-5 h-5" />
                            </div>
                            <span className="text-sm font-bold text-slate-800 uppercase">{ac.itemB} Needed</span>
                          </div>
                          <button data-testid="adblue-toggle" onClick={() => setNeedsAdBlue(!needsAdBlue)}
                            className={`w-14 h-7 rounded-full transition-all relative ${needsAdBlue ? "bg-sky-500" : "bg-slate-700"}`}>
                            <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-all shadow ${needsAdBlue ? "left-7" : "left-0.5"}`} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Transport: destination site */}
              {requestType === "TRANSPORT" && (
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Destination Site</label>
                  <select data-testid="target-site-select"
                    value={targetSiteId} onChange={e => setTargetSiteId(e.target.value)}
                    className="w-full p-3 bg-white border border-slate-200 rounded-xl font-bold text-slate-900 outline-none focus:border-blue-500/40">
                    <option value="">Select destination...</option>
                    {otherSites.map(s => <option key={s.id} value={s.id}>{s.name} / {s.city}</option>)}
                  </select>
                </div>
              )}

              {/* Notes */}
              <textarea data-testid="request-notes" value={requestNotes} onChange={e => setRequestNotes(e.target.value)}
                placeholder="Additional notes..."
                className="w-full p-4 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 h-16 resize-none outline-none focus:border-lime-400 placeholder:text-slate-400" />

              {/* GPS indicator */}
              {gpsCoords.lat !== 0 && (
                <div className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-xl" data-testid="gps-indicator">
                  <Navigation className="w-4 h-4 text-lime-500" />
                  <span className="text-[10px] font-mono text-lime-600">{gpsCoords.lat.toFixed(5)}, {gpsCoords.lon.toFixed(5)}</span>
                  <span className="text-[9px] text-slate-400 ml-auto">Auto-detected</span>
                </div>
              )}
              {gpsLoading && (
                <div className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-xl">
                  <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                  <span className="text-[10px] text-slate-400">Detecting GPS...</span>
                </div>
              )}

              {/* Submit */}
              <button data-testid="send-request-btn" onClick={handleSubmitRequest}
                disabled={requestType === "TRANSPORT" && !targetSiteId}
                className={`w-full py-4 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 transition-all shadow-lg disabled:opacity-30 ${
                  requestType === ac.requestType ? "bg-lime-500 text-slate-950" :
                  requestType === "REPARATION" ? "bg-red-600 text-white" :
                  requestType === "MAINTENANCE" ? "bg-purple-600 text-white" :
                  "bg-blue-600 text-white"
                }`}>
                <Send className="w-5 h-5" /> Send {requestType === ac.requestType ? ac.requestLabel.replace(" Request", "") : requestType} Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {statusMachine && (
        <div className="fixed inset-0 z-[400] bg-white/95 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-slate-50 rounded-xl w-full max-w-sm border border-slate-200 animate-scaleIn">
            <div className="bg-white p-5 flex justify-between items-center border-b border-slate-200">
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">{t("action_center")}</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{statusMachine.machine_number}</p>
              </div>
              <button onClick={() => setStatusMachine(null)} className="p-2 bg-slate-100 rounded-xl text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "NORMAL", label: t("in_use"), icon: <CheckCircle className="w-3.5 h-3.5" />, c: "lime" },
                  { id: "DEFECT", label: t("defect"), icon: <Wrench className="w-3.5 h-3.5" />, c: "red" },
                  { id: "UPKEEP", label: t("maintenance"), icon: <Settings className="w-3.5 h-3.5" />, c: "purple" },
                  { id: "TRANSPORT", label: t("transport_needed"), icon: <Truck className="w-3.5 h-3.5" />, c: "blue" }
                ].map(opt => (
                  <button key={opt.id} data-testid={`status-${opt.id}`} onClick={() => setStatusType(opt.id)}
                    className={`p-3 rounded-xl text-[10px] font-bold uppercase border flex items-center gap-2 transition-all ${statusType === opt.id ? `border-${opt.c}-500 bg-${opt.c}-500/10 text-${opt.c}-400` : "border-slate-200 bg-white text-slate-400"}`}>
                    {opt.icon} {opt.label}
                  </button>
                ))}
              </div>
              <textarea data-testid="status-notes" value={statusNotes} onChange={e => setStatusNotes(e.target.value)}
                placeholder={t("details_reason")}
                className="w-full p-4 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 h-20 resize-none outline-none focus:border-lime-400 placeholder:text-slate-400" />
              <button data-testid="submit-status" onClick={handleStatusSubmit}
                className="w-full py-4 bg-lime-500 text-slate-950 rounded-xl text-sm font-black flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> {t("send_to_planner")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* External Machine Modal */}
      {showExternal && (
        <div className="fixed inset-0 z-[450] bg-white/95 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-slate-50 rounded-xl w-full max-w-sm border border-slate-200 animate-scaleIn">
            <div className="bg-purple-600 p-5 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-white">{t("add_rental")}</h3>
                <p className="text-[10px] text-purple-200 font-bold uppercase tracking-wider mt-1">New machine registration</p>
              </div>
              <button onClick={() => setShowExternal(false)} className="p-2 hover:bg-white/10 rounded-xl text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">{t("machine_type")}</label>
                <div className="grid grid-cols-2 gap-2">
                  {["CRANE", "GENERATOR", "TANK", "OTHER"].map(type => (
                    <button key={type} onClick={() => setExtType(type)}
                      className={`p-3 rounded-xl text-[10px] font-bold uppercase border transition-all ${extType === type ? "border-purple-500 bg-purple-500/10 text-purple-400" : "border-slate-200 bg-white text-slate-400"}`}>
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">{t("fleet_id")}</label>
                <input data-testid="ext-id-input" value={extId} onChange={e => setExtId(e.target.value)}
                  placeholder="e.g. CAT D6R"
                  className="w-full p-4 bg-white border border-slate-200 rounded-xl font-bold text-slate-900 outline-none focus:border-purple-500/50 placeholder:text-slate-400" />
              </div>
              <button data-testid="register-ext" onClick={handleRegisterExternal} disabled={!extId}
                className="w-full py-4 bg-purple-600 text-white rounded-xl text-sm font-black flex items-center justify-center gap-2 disabled:opacity-30">
                <PlusCircle className="w-5 h-5" /> Register Machine
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white p-4 border-b border-slate-200 sticky top-0 z-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center text-slate-950 shadow-[0_0_15px_rgba(245,158,11,0.3)]">
            <HardHat className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">{t("machinist_dashboard")}</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{myName} / {activeSite?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TutorialToggle onClick={() => setShowTutorial(!showTutorial)} />
          <button onClick={() => setActiveSiteId(null)} className="p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button onClick={() => { setSession(null); nav("/portal"); }} data-testid="back-to-lobby-btn"
            className="p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all" title="Back to Lobby">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <LanguageSwitcher compact />
          <button onClick={() => { logout(); nav("/"); }} data-testid="machinist-exit"
            className="p-2.5 bg-red-50 border border-red-200 rounded-xl text-red-500 hover:bg-red-600 hover:text-slate-900 transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="p-4 space-y-5">
        {/* Site Info Card with Edit */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="site-info-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-red-500" /> {t("current_site")}
            </span>
            <button onClick={() => setEditingSite(!editingSite)} data-testid="edit-site-btn"
              className={`px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase flex items-center gap-1 transition-all ${
                editingSite ? "bg-lime-500 text-slate-950" : "bg-slate-100 text-slate-400 hover:text-slate-900 border border-slate-200"
              }`}>
              <Settings2 className="w-3 h-3" /> Edit
            </button>
          </div>

          <select data-testid="site-selector" value={activeSiteId || ""} onChange={e => setActiveSiteId(e.target.value)}
            className="w-full p-3 bg-white border border-slate-200 rounded-xl font-bold text-slate-900 outline-none focus:border-lime-400 mb-3">
            {sites.map(s => <option key={s.id} value={s.id}>{s.name} / {s.city}</option>)}
          </select>

          {/* GPS Pin */}
          {activeSite?.gps_lat > 0 && (
            <a href={`https://www.google.com/maps?q=${activeSite.gps_lat},${activeSite.gps_lon}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 p-2.5 bg-white border border-slate-200 rounded-xl mb-3 hover:border-lime-400 transition-all"
              data-testid="site-gps-link">
              <Navigation className="w-4 h-4 text-lime-500" />
              <span className="text-[10px] font-mono text-lime-600">{activeSite.gps_lat.toFixed(5)}, {activeSite.gps_lon.toFixed(5)}</span>
              <span className="text-[9px] text-slate-400 ml-auto">Open Maps</span>
            </a>
          )}

          {/* Editable fields */}
          {editingSite && (
            <div className="space-y-3 mt-3 p-3 bg-white border border-slate-200 rounded-xl" data-testid="site-edit-panel">
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1 flex items-center gap-1">
                  <Key className="w-3 h-3" /> Access Code
                </label>
                <input data-testid="site-access-code" value={siteAccessCode} onChange={e => setSiteAccessCode(e.target.value)}
                  placeholder="Gate code / lock combination"
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 outline-none focus:border-lime-400 placeholder:text-slate-400" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1 flex items-center gap-1">
                  <Route className="w-3 h-3" /> Access Route
                </label>
                <textarea data-testid="site-access-route" value={siteAccessRoute} onChange={e => setSiteAccessRoute(e.target.value)}
                  placeholder="Best route to access the site..."
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 h-16 resize-none outline-none focus:border-lime-400 placeholder:text-slate-400" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1 flex items-center gap-1">
                  <Settings2 className="w-3 h-3" /> Site Phase / Stage
                </label>
                <select data-testid="site-phase-select" value={sitePhase} onChange={e => setSitePhase(e.target.value)}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 outline-none focus:border-lime-400">
                  <option value="">No Phase Set</option>
                  <option value="Groundwork">Groundwork</option>
                  <option value="Foundation">Foundation</option>
                  <option value="Structure">Structure</option>
                  <option value="Roofing">Roofing</option>
                  <option value="Finishing">Finishing</option>
                  <option value="Landscaping">Landscaping</option>
                  <option value="Demolition">Demolition</option>
                </select>
              </div>
              <button data-testid="save-site-info" onClick={handleSaveSiteInfo} disabled={savingSite}
                className="w-full py-3 bg-lime-500 text-slate-950 rounded-xl text-xs font-black uppercase flex items-center justify-center gap-2 disabled:opacity-50">
                {savingSite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Site Info
              </button>
            </div>
          )}
        </div>

        {/* Machine Pool */}
        <div>
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-lime-500" /> {t("machine_pool")}
            </span>
            <button data-testid="add-rental-btn" onClick={() => setShowExternal(true)}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-xl text-[10px] font-bold uppercase flex items-center gap-1 active:scale-95 transition-all">
              <PlusCircle className="w-3.5 h-3.5" /> Rental
            </button>
          </div>
          <div className="space-y-3">
            {siteMachines.map(m => {
              const hasReq = requests.some(r => r.machine_id === m.id && r.status !== "DONE");
              return (
                <div key={m.id} data-testid={`machine-${m.id}`}
                  className={`bg-slate-50 border rounded-xl p-4 transition-all ${m.is_external ? "border-purple-500/30" : "border-slate-200"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button onClick={() => openRequestModal(m, "FUEL")}
                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold cursor-pointer hover:scale-105 transition-all ${m.is_external ? "bg-purple-600" : hasReq ? "bg-lime-500 text-slate-950" : "bg-slate-700"}`}>
                        {m.is_external ? <Zap className="w-5 h-5" /> : <HardHat className="w-5 h-5" />}
                      </button>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-900 uppercase tracking-tight">{m.machine_number}</span>
                          {m.is_external && <span className="text-[8px] font-bold bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">RENTAL</span>}
                        </div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{m.name}</p>
                        <div className="flex gap-1 mt-1">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 ${getStatusColor(m.status_type)}`}>
                            {getStatusIcon(m.status_type)} {m.status_type || "OK"}
                          </span>
                          {hasReq && <span className="text-[9px] font-bold bg-lime-50 text-lime-600 px-1.5 py-0.5 rounded flex items-center gap-0.5 animate-pulse"><Clock className="w-2.5 h-2.5" /> QUEUED</span>}
                        </div>
                        {m.gps_lat > 0 && (
                          <p className="text-[8px] font-mono text-slate-400 mt-1 flex items-center gap-0.5">
                            <Navigation className="w-2 h-2" /> {m.gps_lat.toFixed(3)}, {m.gps_lon.toFixed(3)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <button onClick={() => { setStatusMachine(m); setStatusType(m.status_type || "NORMAL"); setStatusNotes(m.status_notes || ""); }}
                        className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all" data-testid={`machine-status-${m.id}`}>
                        <Settings2 className="w-4 h-4" />
                      </button>
                      <div className="flex gap-1">
                        <button onClick={() => openRequestModal(m, "FUEL")}
                          className="p-2 bg-lime-500 text-slate-950 rounded-xl active:scale-95 transition-all" data-testid={`fuel-btn-${m.id}`}>
                          <Fuel className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => openRequestModal(m, "REPARATION")}
                          className="p-2 bg-red-600 text-white rounded-xl active:scale-95 transition-all" data-testid={`repair-btn-${m.id}`}>
                          <Wrench className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => openRequestModal(m, "TRANSPORT")}
                          className="p-2 bg-blue-600 text-white rounded-xl active:scale-95 transition-all" data-testid={`transport-btn-${m.id}`}>
                          <ArrowRightLeft className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Request Tracker */}
        {myRecentRequests.length > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="echo-tracker">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-3">
              <Bird className="w-3.5 h-3.5 text-lime-500" /> {t("echo_tracker")}
            </h3>
            <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
              {myRecentRequests.slice(0, 8).map(req => (
                <div key={req.id} className={`min-w-[160px] p-4 rounded-xl border shrink-0 ${req.status === "DONE" ? "border-lime-300 bg-lime-500/5" : "border-slate-200 bg-white"}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-black text-slate-900">{req.machine_number}</span>
                    <div className={`w-2.5 h-2.5 rounded-full ${req.status === "DONE" ? "bg-lime-500" : "bg-red-500 animate-pulse"}`} />
                  </div>
                  <div className="flex items-center gap-1 mb-1">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 ${getReqColor(req.request_type || "FUEL")}`}>
                      {getReqTypeIcon(req.request_type || "FUEL")} {req.request_type || "FUEL"}
                    </span>
                  </div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider truncate">{req.site_name}</p>
                  {req.status === "DONE" ? (
                    <div className="mt-2 p-2 bg-lime-500 text-slate-950 rounded text-[10px] font-black text-center">
                      {ac.isTaskBased ? "COMPLETED" : req.delivered_diesel ? `+${req.delivered_diesel}${ac.itemAUnit}` : "COMPLETED"}
                    </div>
                  ) : (
                    <p className="text-[10px] text-amber-400 font-bold uppercase mt-2">{t("waiting")}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Floating QR Scan Button */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100]">
        <button data-testid="qr-scan-btn" onClick={() => { setIsScanning(true); setTimeout(() => { setIsScanning(false); handleQRScan(); }, 800); }}
          disabled={isScanning}
          className="px-8 py-4 bg-slate-50 text-white rounded-full shadow-[0_10px_40px_rgba(0,0,0,0.6)] border border-slate-200 hover:border-lime-400 flex items-center gap-2">
          {isScanning ? <Loader2 className="w-5 h-5 animate-spin" /> : <QrCode className="w-5 h-5 text-lime-500" />}
          <span className="text-xs font-bold uppercase">{isScanning ? "Scanning..." : "Quick Scan"}</span>
        </button>
      </div>
    </div>
  );
}
